import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, MapPin, Check } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface Trip {
  id: string;
  title: string;
  destination: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  trip_type: string;
  budget: number | null;
  currency: string;
  is_active: boolean;
}

interface TripEditModalProps {
  trip: Trip | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTripUpdated: () => void;
}

const POPULAR_CITIES = [
  { name: 'Paris', country: 'France' },
  { name: 'London', country: 'United Kingdom' },
  { name: 'Tokyo', country: 'Japan' },
  { name: 'New York', country: 'United States' },
  { name: 'Barcelona', country: 'Spain' },
  { name: 'Rome', country: 'Italy' },
  { name: 'Amsterdam', country: 'Netherlands' },
  { name: 'Bangkok', country: 'Thailand' },
  { name: 'Singapore', country: 'Singapore' },
  { name: 'Dubai', country: 'United Arab Emirates' },
  { name: 'Berlin', country: 'Germany' },
  { name: 'Sydney', country: 'Australia' },
  { name: 'Toronto', country: 'Canada' },
  { name: 'Mumbai', country: 'India' },
  { name: 'São Paulo', country: 'Brazil' },
  { name: 'Buenos Aires', country: 'Argentina' },
  { name: 'Istanbul', country: 'Turkey' },
  { name: 'Moscow', country: 'Russia' },
  { name: 'Cairo', country: 'Egypt' },
  { name: 'Cape Town', country: 'South Africa' },
  { name: 'Los Angeles', country: 'United States' },
  { name: 'Miami', country: 'United States' },
  { name: 'Madrid', country: 'Spain' },
  { name: 'Milan', country: 'Italy' },
  { name: 'Athens', country: 'Greece' },
  { name: 'Lisbon', country: 'Portugal' },
];

export function TripEditModal({ trip, open, onOpenChange, onTripUpdated }: TripEditModalProps) {
  const [formData, setFormData] = useState({
    trip_name: '',
    destination: '',
    destination_country: '',
    start_date: undefined as Date | undefined,
    end_date: undefined as Date | undefined,
    trip_type: '',
    budget: '',
    currency: 'USD',
    is_active: true
  });
  const [destinationInput, setDestinationInput] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (trip) {
      setFormData({
        trip_name: trip.title,
        destination: trip.destination,
        destination_country: trip.destination_country,
        start_date: new Date(trip.start_date),
        end_date: new Date(trip.end_date),
        trip_type: trip.trip_type,
        budget: trip.budget?.toString() || '',
        currency: trip.currency,
        is_active: trip.is_active
      });
      setDestinationInput(`${trip.destination}, ${trip.destination_country}`);
    }
  }, [trip]);

  const filteredCities = useMemo(() => {
    if (!destinationInput.trim()) return [];
    return POPULAR_CITIES.filter(city =>
      city.name.toLowerCase().includes(destinationInput.toLowerCase()) ||
      city.country.toLowerCase().includes(destinationInput.toLowerCase())
    ).slice(0, 8);
  }, [destinationInput]);

  const selectCity = (city: { name: string; country: string }) => {
    setFormData(prev => ({ 
      ...prev, 
      destination: city.name,
      destination_country: city.country 
    }));
    setDestinationInput(`${city.name}, ${city.country}`);
    setShowCitySuggestions(false);
  };

  const handleDestinationChange = (value: string) => {
    setDestinationInput(value);
    setShowCitySuggestions(true);
    if (!value.trim()) {
      setFormData(prev => ({ ...prev, destination: '', destination_country: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trip || !formData.start_date || !formData.end_date || !formData.destination) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('trips')
        .update({
          title: formData.trip_name || `Trip to ${formData.destination}`,
          destination: formData.destination,
          destination_country: formData.destination_country,
          start_date: formData.start_date.toISOString().split('T')[0],
          end_date: formData.end_date.toISOString().split('T')[0],
          trip_type: formData.trip_type as any,
          budget: formData.budget ? parseFloat(formData.budget) : null,
          currency: formData.currency,
          is_active: formData.is_active
        })
        .eq('id', trip.id);

      if (error) throw error;

      toast({
        title: "Trip Updated!",
        description: "Your trip has been updated successfully.",
      });
      
      onTripUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating trip:', error);
      toast({
        title: "Error",
        description: "Failed to update trip. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!trip) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Trip</DialogTitle>
          <DialogDescription>
            Update your trip details
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="trip_name">Trip Name</Label>
            <Input
              id="trip_name"
              value={formData.trip_name}
              onChange={(e) => setFormData(prev => ({ ...prev, trip_name: e.target.value }))}
              placeholder="e.g., Summer vacation, Business trip to Tokyo..."
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 relative">
              <Label htmlFor="destination">Destination</Label>
              <div className="relative">
                <Input
                  id="destination"
                  value={destinationInput}
                  onChange={(e) => handleDestinationChange(e.target.value)}
                  onFocus={() => setShowCitySuggestions(true)}
                  placeholder="Start typing a city name..."
                  required
                  className="pr-8"
                />
                <MapPin className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
              </div>
              {showCitySuggestions && filteredCities.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 bg-background border border-input rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredCities.map((city) => (
                    <button
                      key={`${city.name}-${city.country}`}
                      type="button"
                      onClick={() => selectCity(city)}
                      className="w-full px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground flex items-center justify-between"
                    >
                      <span>{city.name}, {city.country}</span>
                      {formData.destination === city.name && formData.destination_country === city.country && (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="trip_type">Trip Type</Label>
              <Select value={formData.trip_type} onValueChange={(value) => setFormData(prev => ({ ...prev, trip_type: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select trip type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="leisure">Leisure</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="adventure">Adventure</SelectItem>
                  <SelectItem value="family">Family</SelectItem>
                  <SelectItem value="romantic">Romantic</SelectItem>
                  <SelectItem value="solo">Solo Travel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.start_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.start_date ? format(formData.start_date, "PPP") : "Pick start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.start_date}
                    onSelect={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.end_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.end_date ? format(formData.end_date, "PPP") : "Pick end date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.end_date}
                    onSelect={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                    initialFocus
                    disabled={(date) => formData.start_date ? date < formData.start_date : false}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget">Budget (Optional)</Label>
              <Input
                id="budget"
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Trip Status</Label>
            <Select 
              value={formData.is_active ? "active" : "archived"} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, is_active: value === "active" }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
